import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag } from 'antd';
import moment from 'moment';
import { getAdminActivityLogs } from '../../services/activityLogService';
import ActivityLogFilters from './ActivityLogFilters';
import ActivityDetailModal from './ActivityDetailModal';

const DEFAULT_FILTERS = {
  search: '',
  module: null,
  actionType: null,
  startDate: null,
  endDate: null,
};

const normalizeLogItem = (log, index) => {
  if (!log) return {};
  return {
    ...log,
    id: log.id || log._id || log.logId || `admin-log-${index}`,
    userName: (log.adminEmail && log.adminEmail.trim()) || log.userName || log.user || log.adminName || log.fullName || log.username || log.email || '—',
    userType: log.userType || 'ADMIN',
    role: log.role || log.adminRole || log.userRole || '—',
    module: log.targetEntityType || log.module || log.moduleName || log.component || log.service || '—',
    action: log.action || log.actionPerformed || log.actionType || log.eventType || log.details || '—',
    description: log.description || log.details || '',
    timestamp: log.createdAt || log.timestamp || log.time || log.date || log.eventTime || log?.approvedAt,
    ipAddress: log.ipAddress || log.ip || log.clientIp || '',
    deviceType: log.deviceType || log.device || log.userAgent || '',
    userId: log.adminUserId || log.userId || log.adminId || log.id || '',
  };
};

const AdminActivityTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchLogs = useCallback(async (page = 1, size = 10, f = appliedFilters) => {
    setLoading(true);
    try {
      const res = await getAdminActivityLogs({
        pageNo: page,
        pageSize: size,
        search: f.search || undefined,
        module: f.module || undefined,
        actionType: f.actionType || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      });

      if (res && res.data) {
        const rawLogs = res.data;
        const totalRecords = res.total || 0;

        const normalized = Array.isArray(rawLogs) ? rawLogs.map(normalizeLogItem) : [];
        setLogs(normalized);
        setPagination({ current: page, pageSize: size, total: totalRecords });
      } else {
        setLogs([]);
        setPagination({ current: page, pageSize: size, total: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch admin activity logs:', err);
      setLogs([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchLogs(1, pagination.pageSize, filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    fetchLogs(1, pagination.pageSize, DEFAULT_FILTERS);
  };

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Admin Name',
      dataIndex: 'userName',
      key: 'userName',
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{name}</span>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag
          style={{
            background: '#f9f0ff',
            color: '#722ed1',
            border: '1px solid #d3adf7',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {role || '—'}
        </Tag>
      ),
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      render: (mod) => (
        <Tag
          style={{
            background: '#f5f5f5',
            color: '#595959',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            fontWeight: 500,
          }}
        >
          {mod}
        </Tag>
      ),
    },
    {
      title: 'Action Performed',
      dataIndex: 'action',
      key: 'action',
      ellipsis: true,
      render: (action) => <span style={{ color: '#333' }}>{action}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => <span style={{ color: '#595959' }}>{desc || '—'}</span>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
      render: (ip) => (
        <span style={{ fontFamily: 'monospace', color: '#595959', fontSize: 12 }}>
          {ip || '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <ActivityLogFilters
        filters={filters}
        onFilterChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        hideUserType
        hideLoginStatus
      />
      <div className="activity-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'activity-row-even' : 'activity-row-odd')}
          className="activity-table"
        />
      </div>
      <ActivityDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        activity={selectedLog}
      />
    </div>
  );
};

export default AdminActivityTab;
